// Core Mini Cart functionality with Accessibility Support

// ==========================================================================
// ACCESSIBILITY: Focusable elements selector for focus trap
// ==========================================================================
const FOCUSABLE_SELECTORS = [
    'button:not([disabled]):not([tabindex="-1"])',
    'a[href]:not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])'
].join(', ');

// Store reference to last focused element before cart opens
let lastFocusedElementBeforeCart = null;

// ==========================================================================
// ACCESSIBILITY: Screen Reader Announcement Helper
// ==========================================================================
function announceToScreenReader(message, priority = 'polite') {
    const regionId = priority === 'assertive' 
        ? 'cart-assertive-region' 
        : 'cart-live-region';
    const region = document.getElementById(regionId);
    
    if (!region) {
        console.warn('Live region not found:', regionId);
        return;
    }
    
    // Clear then set to ensure announcement
    region.textContent = '';
    setTimeout(() => {
        region.textContent = message;
    }, 100);
    
    // Clear after announcement
    setTimeout(() => {
        region.textContent = '';
    }, 5000);
}

// Make globally available
window.announceToScreenReader = announceToScreenReader;

// ==========================================================================
// ACCESSIBILITY: Get focusable elements within container
// ==========================================================================
function getFocusableElements(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTORS))
        .filter(el => el.offsetParent !== null); // Only visible elements
}

// ==========================================================================
// ACCESSIBILITY: Focus trap for modal
// ==========================================================================
function initFocusTrap(container) {
    if (!container) return;
    
    container.addEventListener('keydown', function(e) {
        if (e.key !== 'Tab') return;
        
        const focusable = getFocusableElements(container);
        if (focusable.length === 0) return;
        
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    });
}

// ==========================================================================
// ACCESSIBILITY: Keyboard navigation for cart
// ==========================================================================
function initCartKeyboardNavigation() {
    const cart = document.querySelector('#main-nav');
    if (!cart) return;
    
    // Escape key to close cart
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') {
            const isCartOpen = cart.classList.contains('js-menu__expanded');
            const isModalOpen = document.querySelector('.cross-sell-remove-overlay.is-visible');
            
            if (isModalOpen) {
                // Let the modal handle its own escape
                return;
            }
            
            if (isCartOpen) {
                e.preventDefault();
                closeMiniCartModal();
            }
        }
    });
    
    // Arrow keys for quantity inputs
    cart.addEventListener('keydown', function(e) {
        if (!['ArrowUp', 'ArrowDown'].includes(e.key)) return;
        
        const input = e.target;
        if (!input.classList.contains('quantity-input')) return;
        if (input.disabled || input.readOnly) return;
        
        e.preventDefault();
        const current = parseInt(input.value, 10) || 1;
        const min = parseInt(input.getAttribute('min'), 10) || 1;
        
        if (e.key === 'ArrowUp') {
            input.value = current + 1;
        } else if (e.key === 'ArrowDown' && current > min) {
            input.value = current - 1;
        }
        
        input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    
    // Delete key to remove focused item
    cart.addEventListener('keydown', function(e) {
        if (e.key !== 'Delete') return;
        
        const item = document.activeElement.closest('.firstday-cart__flyout-item');
        if (item) {
            const removeBtn = item.querySelector('.quantity-btn.icon-remove');
            if (removeBtn) {
                removeBtn.click();
            }
        }
    });
    
    // Initialize focus trap
    initFocusTrap(cart);
}

// Money formatting utility
Shopify.money_format = "$--amount--";
Shopify.formatMoney = function(cents, format) {
    if (typeof cents == 'string') { cents = cents.replace('.',''); }
    var value = '';
    var placeholderRegex = /\-\-\s*(\w+)\s*\-\-/;
    var formatString = (format || this.money_format);

    function defaultOption(opt, def) {
        return (typeof opt == 'undefined' ? def : opt);
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultOption(precision, 2);
        thousands = defaultOption(thousands, ',');
        decimal   = defaultOption(decimal, '.');

        if (isNaN(number) || number == null) { return 0; }

        number = (number/100.0).toFixed(precision);

        var parts   = number.split('.'),
            dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
            cents   = parts[1] ? (decimal + parts[1]) : '';

        return dollars + cents;
    }

    switch(formatString.match(placeholderRegex)[1]) {
    case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
    case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
    case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
    case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
};

// UI Icons using icon.liquid system
const UI_ICONS = {
    minus: window.FDMinicartCoreConfig.icons.minus,
    plus: window.FDMinicartCoreConfig.icons.plus,
    close: window.FDMinicartCoreConfig.icons.close,
    remove: window.FDMinicartCoreConfig.icons.remove,
    cart: window.FDMinicartCoreConfig.icons.cart
};

// ==========================================================================
// ACCESSIBILITY: Update ARIA states when cart opens/closes
// ==========================================================================
function updateCartAriaStates(isOpen) {
    const cartButton = document.querySelector('.js-menu__open[data-menu="#main-nav"]');
    const cart = document.querySelector('#main-nav');
    const context = document.querySelector('.js-menu__context');
    
    if (cartButton) {
        cartButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
    
    if (context) {
        if (isOpen) {
            context.removeAttribute('aria-hidden');
            context.removeAttribute('inert');
        } else {
            context.setAttribute('aria-hidden', 'true');
            context.setAttribute('inert', '');
        }
    }
}

// Menu toggle functionality with accessibility enhancements (plain DOM, no jQuery)
(function () {
    // Timestamp of the last cart-drawer open. Used to ignore stray mobile
    // touch/ghost-click events that bleed through onto the overlay right after
    // opening (e.g. the tap that opened the cart from the upsell dialog), which
    // would otherwise immediately re-close the drawer.
    var cartOpenedAt = 0;

    function onReady(fn) {
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
        else fn();
    }

    // Delegated (not bound directly to the buttons) so it also works for cart-icon
    // instances injected/cloned AFTER this runs — e.g. Kameleoon A/B-test variants
    // re-render the desktop header icon, which otherwise never gets a click handler
    // and appears "stuck" (clicking does nothing). Delegation matches at click time.
    document.addEventListener('click', function (e) {
        var trigger = e.target && e.target.closest ? e.target.closest('.js-menu__open') : null;
        if (!trigger) return;
        var menuSelector = trigger.getAttribute('data-menu');
        var menu = menuSelector ? document.querySelector(menuSelector) : null;
        if (!menu) return;
        var isCurrentlyOpen = menu.classList.contains('js-menu__expanded');

        if (!isCurrentlyOpen) {
            // Store last focused element before opening
            lastFocusedElementBeforeCart = document.activeElement;
        }

        menu.classList.toggle('js-menu__expanded');
        if (menu.parentElement) menu.parentElement.classList.toggle('js-menu__expanded');
        document.body.classList.toggle('hidden-overflow');

        var isNowOpen = menu.classList.contains('js-menu__expanded');

        // Update ARIA states
        updateCartAriaStates(isNowOpen);

        if (isNowOpen) {
            cartOpenedAt = Date.now();

            if (typeof window.ensureCartRenderedIfNeeded === 'function') {
                window.ensureCartRenderedIfNeeded().catch(function (err) {
                    console.error('Mini cart: lazy cart render failed:', err);
                });
            }
            if (typeof window.FDCartSyncCheckoutAttributes === 'function') window.FDCartSyncCheckoutAttributes();

            // Focus the checkout button when cart opens
            setTimeout(function () {
                var checkoutButton = document.getElementById('checkoutButton');
                if (checkoutButton && !checkoutButton.disabled) {
                    checkoutButton.focus();
                } else {
                    // Fallback to close button if checkout is disabled (empty cart)
                    var closeButton = menu.querySelector('.js-menu__close');
                    if (closeButton) closeButton.focus();
                }
            }, 100);

            // Announce to screen readers
            var countEl = document.querySelector('.cart-count');
            var itemCount = (countEl && countEl.textContent) || '0';
            announceToScreenReader('Shopping cart opened. ' + itemCount + ' items in cart.');
        } else {
            // Announce cart closed
            announceToScreenReader('Shopping cart closed.');
        }
    });

    function onContextOrClose(event) {
        // Ignore stray taps within a short window of opening. On mobile a
        // touchend + synthesized click (and ghost-clicks ~300ms later) from the
        // control that opened the cart can bleed through onto the overlay and
        // instantly re-close the drawer (notably after the upsell dialog).
        if (Date.now() - cartOpenedAt < 450) {
            return;
        }
        // Use closest() rather than a class check so clicks on descendants of the
        // close button (e.g. the SVG path inside) still register as a close.
        var target = event.target;
        var clickedBackground = !!(target.classList && target.classList.contains('js-menu__context'));
        var clickedCloseButton = !!(target.closest && target.closest('.js-menu__close'));
        if (clickedBackground || clickedCloseButton) {
            // On mobile, a `touchend` on the X (or overlay) is followed ~300ms
            // later by a synthesized "ghost click" at the same coordinates. Once
            // the cart closes, that click bleeds through onto the full-width
            // announcement-bar sale trigger underneath and launches the sale
            // dialog. preventDefault() on touchend cancels the compatibility
            // mouse events so the ghost click is never generated. (Harmless on
            // desktop, which only fires `click`.)
            if (event.type === 'touchend') {
                event.preventDefault();
            }
            setTimeout(function () {
                Array.prototype.forEach.call(document.querySelectorAll('.js-menu__expanded'), function (el) {
                    el.classList.remove('js-menu__expanded');
                });
                document.body.classList.remove('hidden-overflow');

                // Update ARIA states
                updateCartAriaStates(false);

                // Return focus to the element that opened the cart
                if (lastFocusedElementBeforeCart && typeof lastFocusedElementBeforeCart.focus === 'function') {
                    lastFocusedElementBeforeCart.focus();
                }

                // Announce to screen readers
                announceToScreenReader('Shopping cart closed.');
            }, 1);
        }
    }

    onReady(function () {
        Array.prototype.forEach.call(document.querySelectorAll('.js-menu__context, .js-menu__close'), function (el) {
            el.addEventListener('touchend', onContextOrClose);
            el.addEventListener('click', onContextOrClose);
        });

        // Initialize keyboard navigation
        initCartKeyboardNavigation();
    });
})();

// Core cart functions
function toggleCartOpen(){
    // Programmatic "open cart after add". Every caller uses this to OPEN the
    // drawer, never to close it — so it must be idempotent. The underlying
    // click handler on .js-menu__open is a TOGGLE, so blindly clicking when the
    // drawer is already open (e.g. adding a second item, stale expanded state,
    // or a mobile touch+click double-fire) would instead CLOSE it. That race is
    // why the cart "sometimes" failed to open on mobile. Only click to open when
    // it's currently closed; if already open, leave it open.
    const cartButton = document.querySelector('.js-menu__open[data-menu="#main-nav"]');
    if (!cartButton) return;
    const menu = document.querySelector('#main-nav');
    const context = document.querySelector('.js-menu__context');

    // The drawer's VISIBLE open is driven by the WRAPPER (.js-menu__context) having
    // js-menu__expanded AND being interactive (not inert) — that's what the CSS keys
    // off. #main-nav itself stays on-screen via opacity even when closed, so its
    // class/rect can't be trusted: a stale js-menu__expanded on #main-nav made the
    // old guard think the cart was open and skip the click, so it never auto-opened
    // after add (the shopper had to tap the cart icon).
    const trulyOpen = !!context
        && context.classList.contains('js-menu__expanded')
        && !context.hasAttribute('inert');
    if (trulyOpen) return; // already open and interactive — leave it open

    // Normalize the (possibly stale) expanded flags to closed so the toggle click
    // reliably OPENS. The click handler then does the real open — sets cartOpenedAt
    // for the mobile ghost-click guard, focus, aria, and removes inert.
    if (menu) menu.classList.remove('js-menu__expanded');
    if (context) context.classList.remove('js-menu__expanded');
    cartButton.click();
}

window.toggleCartOpen = toggleCartOpen;

function clearCart(){
    console.log('clear cart');
    var clearPromise = window.FDCartApi
        ? window.FDCartApi.clearCart()
        : fetch('/cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        }).then(function (response) { return response.json(); });

    clearPromise
    .then(function (data) {
        console.log('Cart cleared:', data);
        announceToScreenReader('Cart cleared.');
        if (typeof updateCartDisplay === 'function') {
            updateCartDisplay(data);
        }
        if (typeof updateCartCount === 'function') {
            updateCartCount(data.item_count || 0);
        }
    })
    .catch(function (error) {
        console.error('Error clearing cart:', error);
        announceToScreenReader('Error clearing cart.', 'assertive');
    });
}

function closeMiniCartModal(){
    const menu = document.querySelector('#main-nav');
    const context = document.querySelector('.js-menu__context');

    if (menu && context) {
        menu.classList.remove('js-menu__expanded');
        context.classList.remove('js-menu__expanded');
        document.body.classList.remove('hidden-overflow');
        
        // Update ARIA states
        updateCartAriaStates(false);
        
        // Return focus to the element that opened the cart
        if (lastFocusedElementBeforeCart && typeof lastFocusedElementBeforeCart.focus === 'function') {
            lastFocusedElementBeforeCart.focus();
        }
        
        // Announce to screen readers
        announceToScreenReader('Shopping cart closed.');
    }
}

// ==========================================================================
// ACCESSIBILITY: Announce package protection toggle state changes
// ==========================================================================
function announceProtectionState(checkbox) {
    if (!checkbox) return;
    
    const isEnabled = checkbox.checked;
    const message = isEnabled 
        ? 'Package protection enabled for $2.98'
        : 'Package protection disabled';
    
    // Update aria-checked attribute
    checkbox.setAttribute('aria-checked', isEnabled ? 'true' : 'false');
    
    // Announce to screen readers
    if (typeof announceToScreenReader === 'function') {
        announceToScreenReader(message);
    }
}
